/**
 * Makes the product gallery video actually autoplay.
 *
 * Loaded on every page from snippets/scripts.liquid.
 *
 * THE PROBLEM, diagnosed 10 October 2026. Horizon renders the gallery's media list more than
 * once — desktop grid, mobile carousel, thumbnails — and hides all but one copy with CSS. Each
 * copy of an autoplay video still tries to play, so three identical 1080p / 7.2 Mbps streams
 * compete for the browser's video decoders. Measured on the live Swan page: all three
 * oscillated play/pause about twice a second and advanced roughly 0.01s per second, so nothing
 * ever visibly ran. No script was pausing them — the browser was, and it never settles on a
 * winner. iOS Safari is stricter again, permitting one playing video at a time, which is why a
 * phone showed nothing but a play button while desktop sometimes limped along.
 *
 * THE FIX. Let only the copy that CSS is actually showing play, and make the hidden copies
 * stand down. A hidden copy has no layout box, so offsetWidth/offsetHeight are 0 — that is the
 * test, and it is layout-agnostic, so it keeps working when the breakpoint changes which copy
 * is visible.
 *
 * The pause is wired to each video's own 'play' event rather than done once on load, because
 * the browser keeps retrying autoplay on the hidden copies. Policing it at the source means no
 * polling and no timers.
 *
 * Deliberately does NOT force the visible video to keep playing. Browsers pause a muted
 * autoplay video that scrolls out of view and resume it when it comes back, which is the
 * behaviour we want; fighting it would waste a bride's battery for no gain.
 */
(function () {
  'use strict';

  var SELECTOR = 'deferred-media[autoplay] video';
  var wired = typeof WeakSet === 'function' ? new WeakSet() : null;
  var touched = typeof WeakSet === 'function' ? new WeakSet() : null;

  function shown(el) {
    return !!(el.offsetWidth && el.offsetHeight);
  }

  function wire(video) {
    if (!wired || wired.has(video)) return;
    wired.add(video);

    // The browser retries autoplay on the hidden copies, so police every play attempt.
    video.addEventListener('play', function () {
      if (!shown(video)) video.pause();
    });

    // If the bride uses the play/pause control herself, her choice wins from then on.
    var host = video.closest ? video.closest('deferred-media') : null;
    if (host && touched) {
      host.addEventListener('pointerdown', function () {
        touched.add(video);
      });
    }
  }

  function apply() {
    var videos = document.querySelectorAll(SELECTOR);
    if (!videos.length) return;

    var live = null;
    var i;

    // Hidden copies first: the visible one cannot get a decoder until they let go.
    for (i = 0; i < videos.length; i++) {
      wire(videos[i]);
      if (shown(videos[i])) {
        if (!live) live = videos[i];
      } else if (!videos[i].paused) {
        videos[i].pause();
      }
    }

    // By the time this runs, the contention above has usually left the visible copy paused
    // partway through, so it needs starting by hand. Muted, or the browser will refuse.
    if (live && live.paused && !(touched && touched.has(live))) {
      live.muted = true;
      var started = live.play();
      if (started && started.catch) started.catch(function () {});
    }
  }

  var pending = null;
  function applySoon() {
    if (pending) clearTimeout(pending);
    pending = setTimeout(function () {
      pending = null;
      apply();
    }, 150);
  }

  function start() {
    apply();

    // The browser is often still resolving the decoder contention when the first apply()
    // runs, and it can pause the visible copy again immediately afterwards. Measured on the
    // live Swan page: the video sat paused 1.3s in for several seconds and only recovered
    // when an unrelated app happened to mutate the DOM and trip the observer below. These
    // few staggered retries remove that dependence on luck. Bounded deliberately — apply()
    // leaves an already-playing video alone, so once it settles these do nothing.
    [300, 900, 2000, 4000].forEach(function (delay) {
      setTimeout(apply, delay);
    });
    // The gallery is re-rendered when a variant is chosen, which replaces the video elements.
    if (typeof MutationObserver === 'function') {
      new MutationObserver(applySoon).observe(document.body, { childList: true, subtree: true });
    }
    // A breakpoint change swaps which copy is visible.
    window.addEventListener('resize', applySoon);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
